/**
 * Lire et écrire la fiche d'un utilisateur, depuis l'administration.
 *
 * ## Aucune migration n'a été nécessaire
 *
 * La politique « Admins can update all users » existe déjà sur `public.users`
 * (UPDATE, `USING` et `WITH CHECK` sur `get_user_role(auth.uid()) = 'admin'`).
 * C'est elle, et elle seule, qui autorise cette écriture — il n'y a pas de
 * contrôle de rôle dans ce module, parce qu'il serait un second endroit où se
 * tromper.
 *
 * ## Le client typé, ici, convient
 *
 * `public.users` est entièrement décrit dans `src/lib/database.types.ts` : les
 * quatorze colonnes y sont, en `Row`, `Insert` et `Update`. Ce module n'a donc
 * pas besoin du client re-typé de `payments/db.ts`, et vaut mieux sans lui.
 */

import { supabase } from '../supabase';
import { messageErreur } from '../payments/erreur';
import type { Fiche, FicheEnLecture } from './fiche';
import { filtresDeRecherche } from './recherche';

export interface FicheComplete {
  fiche: Fiche;
  lecture: FicheEnLecture;
}

const CHAMPS =
  'id, email, prenom, nom, adresse, cpostal, localite, telephone, telephone2, '
  + 'nnational, account_type, organisation_name, company_number, is_legal_guardian, '
  + 'role, newsletter, created_at';

export async function lireFicheUtilisateur(userId: string): Promise<FicheComplete | null> {
  const { data, error } = await supabase
    .from('users')
    .select(CHAMPS)
    .eq('id', userId)
    .maybeSingle();
  if (error) throw new Error(messageErreur(error, 'Fiche illisible'));
  if (!data) return null;

  const l = data as unknown as Ligne;
  return {
    fiche: {
      email: l.email ?? '',
      prenom: l.prenom,
      nom: l.nom,
      adresse: l.adresse,
      cpostal: l.cpostal,
      localite: l.localite,
      telephone: l.telephone,
      telephone2: l.telephone2,
      nnational: l.nnational,
      accountType: l.account_type ?? 'parent',
      organisationName: l.organisation_name,
      companyNumber: l.company_number,
      isLegalGuardian: l.is_legal_guardian ?? false,
    },
    lecture: {
      id: l.id,
      role: l.role ?? 'parent',
      newsletter: l.newsletter ?? false,
      creeLe: l.created_at,
    },
  };
}

/**
 * Écrit les seuls champs modifiés.
 *
 * `champsModifies` a déjà fait le tri : passer un objet vide ici n'écrit rien,
 * ce qui est le bon comportement et non un cas limite à traiter.
 */
export async function majFicheUtilisateur(
  userId: string,
  champs: Partial<Fiche>,
): Promise<void> {
  const colonnes: Record<string, unknown> = {};
  const nom: Record<keyof Fiche, string> = {
    email: 'email', prenom: 'prenom', nom: 'nom', adresse: 'adresse',
    cpostal: 'cpostal', localite: 'localite', telephone: 'telephone',
    telephone2: 'telephone2', nnational: 'nnational',
    accountType: 'account_type', organisationName: 'organisation_name',
    companyNumber: 'company_number', isLegalGuardian: 'is_legal_guardian',
  };
  for (const [cle, valeur] of Object.entries(champs)) {
    const colonne = nom[cle as keyof Fiche];
    if (colonne) colonnes[colonne] = valeur;
  }
  if (Object.keys(colonnes).length === 0) return;

  const { error } = await supabase.from('users').update(colonnes).eq('id', userId);
  if (error) throw new Error(messageErreur(error, "L'enregistrement a été refusé"));
}

interface Ligne {
  id: string;
  email: string | null;
  prenom: string | null;
  nom: string | null;
  adresse: string | null;
  cpostal: string | null;
  localite: string | null;
  telephone: string | null;
  telephone2: string | null;
  nnational: string | null;
  account_type: 'parent' | 'organisation' | 'other' | null;
  organisation_name: string | null;
  company_number: string | null;
  is_legal_guardian: boolean | null;
  role: string | null;
  newsletter: boolean | null;
  created_at: string | null;
}

// ---------------------------------------------------------------------------
//  Chercher un compte
// ---------------------------------------------------------------------------

export interface CompteTrouve {
  id: string;
  email: string;
  prenom: string | null;
  nom: string | null;
  localite: string | null;
  cpostal: string | null;
  telephone: string | null;
  accountType: 'parent' | 'organisation' | 'other';
  organisationName: string | null;
  role: string;
}

export interface PageDeComptes {
  comptes: CompteTrouve[];
  total: number;
  pages: number;
}

/**
 * Les comptes qui répondent à une recherche, par page. Lecture seule : la
 * modification passe par la fiche (`majFicheUtilisateur`), et nulle part
 * ailleurs.
 */
export async function chercherDesComptes(p: {
  recherche: string;
  page: number;
  parPage: number;
}): Promise<PageDeComptes> {
  let requete = supabase
    .from('users')
    .select('id, email, prenom, nom, localite, cpostal, telephone, account_type, organisation_name, role', {
      count: 'exact',
    });
  for (const filtre of filtresDeRecherche(p.recherche)) requete = requete.or(filtre);

  const debut = Math.max(0, (p.page - 1) * p.parPage);
  const { data, error, count } = await requete
    .order('nom', { ascending: true, nullsFirst: false })
    .order('prenom', { ascending: true, nullsFirst: false })
    // Un second critère stable : sans lui, deux homonymes changent d'ordre
    // d'une page à l'autre.
    .order('id', { ascending: true })
    .range(debut, debut + p.parPage - 1);
  if (error) throw new Error(messageErreur(error, 'Recherche des comptes impossible'));

  const total = count ?? 0;
  return {
    comptes: ((data ?? []) as unknown as Ligne[]).map((l) => ({
      id: l.id,
      email: l.email ?? '',
      prenom: l.prenom,
      nom: l.nom,
      localite: l.localite,
      cpostal: l.cpostal,
      telephone: l.telephone,
      accountType: l.account_type ?? 'parent',
      organisationName: l.organisation_name,
      role: l.role ?? 'parent',
    })),
    total,
    pages: Math.max(1, Math.ceil(total / p.parPage)),
  };
}
